import { useEffect, useState, type FormEvent } from 'react'
import { Link } from 'react-router-dom'
import { api, ApiError, createOrQueue } from '../api.ts'

interface Duplicate {
  id: string
  name: string
  city: string | null
  nip: string | null
  phone: string | null
  reason: 'nip' | 'telefon'
}

interface DaneNip {
  nip: string
  name: string
  regon: string | null
  statusVat: string | null
  street: string | null
  postalCode: string | null
  city: string | null
}

/**
 * Add a client fast (SPEC 5.2: "lub dodaj w locie: nazwa + telefon"). A NIP,
 * when there is one, fills the name and address from the VAT white list; a
 * flat-rate farmer has no entry there and is typed in as before.
 *
 * Duplicate check runs as you type, but never blocks or merges on its own: it
 * asks (W13). Two brothers on one farm legitimately share a phone number.
 */
export function NewClientDialog({ onClose, onCreated }: { onClose: () => void; onCreated: (id: string) => void }) {
  const [nip, setNip] = useState('')
  const [name, setName] = useState('')
  const [phone, setPhone] = useState('')
  const [street, setStreet] = useState('')
  const [postalCode, setPostalCode] = useState('')
  const [city, setCity] = useState('')
  // REGON travels with the NIP it was looked up for — never saved under another.
  const [regon, setRegon] = useState<{ nip: string; value: string | null } | null>(null)
  const [nipInfo, setNipInfo] = useState<{ tone: 'ok' | 'warn' | 'err'; text: string } | null>(null)
  const [lookingUp, setLookingUp] = useState(false)
  const [duplicates, setDuplicates] = useState<Duplicate[]>([])
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [queued, setQueued] = useState(false)

  // Ask the server "seen this one before?" while typing.
  useEffect(() => {
    if (!phone.trim() && !nip.trim()) {
      setDuplicates([])
      return
    }
    const t = setTimeout(() => {
      api
        .post<{ duplicates: Duplicate[] }>('/api/clients/check-duplicate', { phone: phone || null, nip: nip || null })
        .then((r) => setDuplicates(r.duplicates))
        .catch(() => setDuplicates([]))
    }, 300)
    return () => clearTimeout(t)
  }, [phone, nip])

  const nipCyfry = nip.replace(/\D/g, '')

  async function pobierzZNip() {
    setLookingUp(true)
    setNipInfo(null)
    try {
      const r = await api.get<{ dane: DaneNip | null }>(`/api/clients/nip/${nipCyfry}`)
      if (!r.dane) {
        setNipInfo({ tone: 'warn', text: 'Brak w rejestrze VAT — pewnie rolnik ryczałtowy. Wpisz dane ręcznie.' })
        return
      }
      const d = r.dane
      // Fill only what's empty — don't overwrite what the user already typed.
      if (!name.trim()) setName(d.name)
      if (!street.trim() && d.street) setStreet(d.street)
      if (!postalCode.trim() && d.postalCode) setPostalCode(d.postalCode)
      if (!city.trim() && d.city) setCity(d.city)
      setRegon({ nip: nipCyfry, value: d.regon })
      setNipInfo({ tone: 'ok', text: `Pobrano z rejestru VAT (${d.statusVat ?? 'status nieznany'}). Sprawdź i popraw, jeśli trzeba.` })
    } catch (e) {
      setNipInfo({ tone: 'err', text: e instanceof ApiError ? e.message : 'Nie udało się pobrać danych.' })
    } finally {
      setLookingUp(false)
    }
  }

  // Ten digits typed → look it up without a click.
  useEffect(() => {
    if (nipCyfry.length !== 10) {
      setNipInfo(null)
      return
    }
    void pobierzZNip()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nipCyfry])

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    setBusy(true)
    try {
      const r = await createOrQueue<{ client: { id: string } }>('client', '/api/clients', {
        name,
        phone: phone || null,
        nip: nipCyfry || null,
        regon: regon && regon.nip === nipCyfry ? regon.value : null,
        street: street || null,
        postalCode: postalCode || null,
        city: city || null,
      })
      if (r.queued) {
        // No connection — the client is in the outbox and will sync on reconnect.
        setQueued(true)
      } else {
        onCreated(r.data.client.id)
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Nie udało się zapisać')
      setBusy(false)
    }
  }

  const INPUT = 'w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:border-brand-600'

  if (queued) {
    return (
      <div className="fixed inset-0 z-50 bg-slate-900/30 flex items-start justify-center pt-20" onClick={onClose}>
        <div className="w-full max-w-md bg-white rounded-lg shadow-xl border border-slate-200 p-5" onClick={(e) => e.stopPropagation()}>
          <h2 className="font-bold text-slate-800 mb-1">Zapisano lokalnie</h2>
          <p className="text-sm text-slate-600">
            Jesteś offline. Klient <strong>{name}</strong> czeka w kolejce i wyśle się automatycznie, gdy wróci sieć.
          </p>
          <div className="flex justify-end mt-4">
            <button onClick={onClose} className="text-sm bg-brand-600 text-white px-4 py-2 rounded-md hover:bg-brand-700">
              OK
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/30 flex items-start justify-center pt-20" onClick={onClose}>
      <form
        onSubmit={onSubmit}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md bg-white rounded-lg shadow-xl border border-slate-200 p-5 space-y-3"
      >
        <h2 className="font-bold text-slate-800">Nowy klient</h2>
        <p className="text-xs text-slate-400 -mt-2">Masz NIP? Wpisz go, a nazwa i adres wypełnią się same. Bez NIP-u wystarczy nazwa.</p>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">NIP</label>
          <div className="flex gap-2">
            <input
              autoFocus
              value={nip}
              onChange={(e) => setNip(e.target.value)}
              placeholder="10 cyfr — puste u rolnika ryczałtowego"
              className={`${INPUT} font-mono`}
            />
            <button
              type="button"
              onClick={() => void pobierzZNip()}
              disabled={nipCyfry.length !== 10 || lookingUp}
              className="shrink-0 text-sm px-3 py-2 border border-slate-300 rounded-md hover:border-brand-600 disabled:opacity-40"
              title="Pobierz nazwę i adres z rejestru VAT"
            >
              {lookingUp ? 'Pobieram…' : 'Pobierz dane'}
            </button>
          </div>
          {nipInfo && (
            <div className={`text-xs mt-1 ${nipInfo.tone === 'ok' ? 'text-green-700' : nipInfo.tone === 'warn' ? 'text-amber-700' : 'text-red-700'}`}>{nipInfo.text}</div>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Nazwa *</label>
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="np. Gospodarstwo Sadownicze Kowalski" className={INPUT} />
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Ulica i numer</label>
          <input value={street} onChange={(e) => setStreet(e.target.value)} className={INPUT} />
        </div>

        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Kod</label>
            <input value={postalCode} onChange={(e) => setPostalCode(e.target.value)} placeholder="05-660" className={INPUT} />
          </div>
          <div className="col-span-2">
            <label className="block text-sm font-medium text-slate-700 mb-1">Miasto</label>
            <input value={city} onChange={(e) => setCity(e.target.value)} className={INPUT} />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Telefon</label>
          <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="601 234 567" className={INPUT} />
        </div>

        {/* A question, never an automatic merge (W13). */}
        {duplicates.length > 0 && (
          <div className="text-sm bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
            <div className="font-medium text-amber-800 mb-1">To chyba ten sam klient?</div>
            <ul className="space-y-1">
              {duplicates.map((d) => (
                <li key={d.id} className="text-amber-900 text-xs">
                  <Link to={`/klienci/${d.id}`} className="underline font-medium" onClick={onClose}>
                    {d.name}
                  </Link>
                  {d.city && <span className="text-amber-700"> · {d.city}</span>}
                  <span className="text-amber-600"> (ten sam {d.reason})</span>
                </li>
              ))}
            </ul>
            <div className="text-xs text-amber-700 mt-1">
              {duplicates.some((d) => d.reason === 'nip')
                ? 'Ten NIP jest już w bazie — zapis się nie uda. Otwórz istniejącego klienta zamiast dodawać drugiego.'
                : 'Możesz mimo to zapisać — np. gdy to brat z tym samym numerem.'}
            </div>
          </div>
        )}

        {error && (
          <div role="alert" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">
            {error}
          </div>
        )}

        <div className="flex gap-2 pt-1">
          <button type="button" onClick={onClose} className="text-sm text-slate-500 px-3 py-1.5 hover:text-slate-700">
            Anuluj
          </button>
          <button
            type="submit"
            disabled={busy || !name.trim()}
            className="text-sm bg-brand-600 text-white px-4 py-1.5 rounded-md hover:bg-brand-700 disabled:opacity-50"
          >
            {busy ? 'Zapisuję…' : 'Zapisz'}
          </button>
        </div>
      </form>
    </div>
  )
}
